import { type OnlineCourseWebCMS } from "@repo/common/src/types/firestore/cms/onlineCourseWeb";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { buildCollection, buildEntityCallbacks, buildProperty, type EntityOnSaveProps } from "@firecms/core";

import { standardPermissions } from "../../utils/constants";
import { loadingOverlay } from "../../utils/loadOverlay";
import { Groups } from "../types";

const callbacks = buildEntityCallbacks({
  onPreSave: async (props: EntityOnSaveProps<OnlineCourseWebCMS>) => {
    loadingOverlay.show();
    const { values } = props;
    return values;
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

export const onlineCoursesWeb = buildCollection<OnlineCourseWebCMS>({
  id: CMSCollections.OnlineCoursesWeb,
  name: "Online Kurse Web",
  singularName: "Online Kurs",
  description: "Kurse in EloPage für die Webseite",
  path: CMSCollections.OnlineCoursesWeb,
  icon: "School",
  group: Groups.Courses,
  permissions: () => standardPermissions,
  callbacks,
  properties: {
    published: {
      name: "Veröffentlicht",
      dataType: "boolean",
      description: "Soll der Kurs auf der App sichtbar sein?",
    },
    name: {
      name: "Name",
      validation: { required: true },
      dataType: "string",
    },
    description: {
      name: "Beschreibung",
      validation: { required: true },
      dataType: "string",
      multiline: true,
    },
    price: {
      name: "Preis",
      validation: {
        required: true,
        min: 0,
      },
      dataType: "number",
    },
    paymentFrequency: {
      name: "Zahlungsfrequenz",
      validation: { required: true },
      dataType: "string",
      enumValues: {
        "one-time": "Einmalig",
        monthly: "Monatlich",
        yearly: "Jährlich",
      },
    },
    paymentLink: {
      name: "Zahlungslink",
      validation: { required: true, matches: /^https?:\/\/.+/, matchesMessage: "Bitte gebe einen gültigen Link ein." },
      dataType: "string",
      url: true,
    },
    features: {
      name: "Features",
      validation: { required: true },
      dataType: "array",
      of: {
        dataType: "map",
        properties: {
          value: {
            name: "Feature",
            validation: { required: true },
            dataType: "string",
          },
        },
      },
    },
    image: buildProperty({
      name: "Kursbild",
      description: "Bild des Kurses. Bitte in 1024x1024 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.OnlineCoursesWebImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxHeight: 1024,
          maxWidth: 1024,
        },
      },
    }),
    landingPageUrl: {
      name: "Landingpage URL",
      description: "Interner Link zur Landingpage des Kurses",
      validation: { required: true },
      dataType: "string",
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
  },
});
